import React, { useEffect, useState } from 'react';
import Chart from 'react-apexcharts';
import { useNavigate } from 'react-router-dom';

// import { ReactComponent as NavigateArrow } from '../../../assets/icons/dashboard/src/assets/icons/dashboard/navigate-arrow.svg';
import { chartTimePeriod } from '../../../config/enum';
import CustomToggleButton from '../../../shared-components/custom-toggle-button/CustomToggleButton';
import dashboard from '../../../utils/api/dashboard';

interface ChartDataProperties {
  label: string;
  data: number[];
}

/**
 * Device utilization report chart
 * @returns
 */
function DeviceUtilizationChart() {
  const navigate = useNavigate();
  const [barGraphData, setBarGraphData] = useState<ChartDataProperties[]>([]);
  const [chartOptions, setChartOptions] = useState<ApexCharts.ApexOptions>({
    chart: {
      type: 'heatmap',
      toolbar: { show: false },
    },
    plotOptions: {
      heatmap: {
        shadeIntensity: 0.5,
        radius: 1,
        useFillColorAsStroke: true,
        colorScale: {
          ranges: [],
        },
      },
    },
    dataLabels: {
      enabled: false,
    },
    legend: {
      position: 'bottom', // If you want to move the legend to the bottom as well
    },
  });

  /**
   * Calculate median value and update chart options
   */
  const findMedianValueFromData = (chartData: ChartDataProperties[]) => {
    const allValues = chartData
      .flatMap((month) => month.data)
      .filter((value) => typeof value === 'number' && !Number.isNaN(value) && value !== 0);

    if (allValues.length === 0) {
      return;
    }

    const sorted = [...allValues].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    const median = sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid];
    const roundedMedian = Math.round(median);

    // Update chart options with new color scale
    setChartOptions((previous) => ({
      ...previous,
      plotOptions: {
        heatmap: {
          ...previous.plotOptions?.heatmap,
          colorScale: {
            ranges: [
              { from: 0, to: roundedMedian, name: 'Low', color: '#2AA700' },
              {
                from: roundedMedian,
                to: Math.max(...allValues) || roundedMedian * 2,
                name: 'High',
                color: '#E41010',
              },
            ],
          },
        },
      },
    }));
  };

  /**
   * Fetch power consumed data based on time range
   */
  const fetchPowerConsumedReport = async (
    range: 'last24hours' | 'last7days' | 'lastMonth' | 'last12months',
  ) => {
    try {
      const response = await dashboard.getDeviceUtilizationReport({
        timePeriod: range,
      });

      if (range === 'last12months') {
        setBarGraphData(response?.data || []);
      } else {
        setBarGraphData(
          response?.data?.map((eachData: any) => ({
            ...eachData,
            name: eachData?.name?.replace(/Z$/, ''),
          })),
        );
      }
      findMedianValueFromData(response?.data || []);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    fetchPowerConsumedReport('last12months');
  }, []);

  return (
    <div className="dashboard-device-utilization-chart-wrap">
      <div className="dashboard-device-utilization-chart-header">
        <div className="header-title">
          <h6>Utilization (Device)</h6>
          <span
            className="header-arrow"
            role="presentation"
            onClick={() => navigate('/ev-admin/device-management')}>
            {/* <NavigateArrow /> */}
          </span>
        </div>
        <CustomToggleButton
          labels={chartTimePeriod.map((duration) => duration.label)}
          onToggle={(index, label) =>
            fetchPowerConsumedReport(
              chartTimePeriod.find((duration) => duration.label === label)?.value || 'last12months',
            )
          }
          defaultActiveIndex={0}
          type="chart"
        />
      </div>
      <div className="dashboard-device-utilization-chart-container">
        <Chart options={chartOptions} series={barGraphData} type="heatmap" height={350} />
      </div>
    </div>
  );
}

export default DeviceUtilizationChart;
